/**
 * RegionCombobox — region filter with search inside: "Все регионы (Мир)" and countries grouped by part of the world,
 * each with its flag. Search matches the country name and code ("бра", "BR").
 *
 * @param {string} value — country code or 'all'
 * @param {(value: string) => void} onChange
 * @param {string} className — classes for the trigger button (width, height)
 */
import React, { useState } from 'react';
import { Check, ChevronDown, Globe } from 'lucide-react';

import { Button } from './ui/button';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
} from './ui/command';
import { FlagIcon } from './GeoFlag';
import { REGIONS, WORLDWIDE } from '../constants/regions';
import { cn } from '../lib/utils';

const ALL = 'all';

function findCountry(code) {
    for (const group of REGIONS) {
        const country = group.countries.find((c) => c.code === code);
        if (country) return country;
    }
    return null;
}

const RegionCombobox = ({ value, onChange, className = '', align = 'start' }) => {
    const [open, setOpen] = useState(false);
    const selected = value && value !== ALL ? findCountry(value) : null;

    const pick = (code) => {
        onChange(code);
        setOpen(false);
    };

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={open}
                    className={cn('justify-between gap-2 font-normal', className)}
                >
                    <span className="flex min-w-0 items-center gap-2">
                        {selected ? (
                            <FlagIcon geo={selected.code} />
                        ) : (
                            <Globe className="h-4 w-4 shrink-0 text-muted-foreground" />
                        )}
                        <span className="truncate">{selected ? selected.name : WORLDWIDE.name}</span>
                    </span>
                    <ChevronDown className="h-4 w-4 shrink-0 opacity-50" />
                </Button>
            </PopoverTrigger>
            {/* The list is as wide as its button (not narrower than the longest country name) */}
            <PopoverContent align={align} className="w-[var(--radix-popover-trigger-width)] min-w-[220px] p-0">
                <Command>
                    <CommandInput placeholder="Страна или код…" />
                    <CommandList className="max-h-[320px]">
                        <CommandEmpty>Ничего не найдено</CommandEmpty>
                        <CommandGroup>
                            <CommandItem
                                value={ALL}
                                keywords={[WORLDWIDE.name, 'мир', 'все']}
                                onSelect={() => pick(ALL)}
                            >
                                <Globe className="h-4 w-4 shrink-0 text-muted-foreground" />
                                <span className="flex-1">{WORLDWIDE.name}</span>
                                {!selected && <Check className="h-4 w-4 shrink-0" />}
                            </CommandItem>
                        </CommandGroup>
                        {REGIONS.map((group) => (
                            <CommandGroup key={group.label} heading={group.label}>
                                {group.countries.map((country) => (
                                    <CommandItem
                                        key={country.code}
                                        value={country.code}
                                        keywords={[country.name, country.code]}
                                        onSelect={() => pick(country.code)}
                                    >
                                        <FlagIcon geo={country.code} />
                                        <span className="flex-1 truncate">{country.name}</span>
                                        <span className="text-xs text-muted-foreground">{country.code}</span>
                                        {selected?.code === country.code && <Check className="h-4 w-4 shrink-0" />}
                                    </CommandItem>
                                ))}
                            </CommandGroup>
                        ))}
                    </CommandList>
                </Command>
            </PopoverContent>
        </Popover>
    );
};

export default RegionCombobox;
